/**
 * @file export.api.js
 * @module ExportAPI
 * @description HTTP API wrapper for export endpoints (data export / report generation). Methods return Axios promises.
 */

import { axiosInstance } from "./axios";

export const ExportAPI = {

    /**
     * Export customers to CSV via POST payload.
     * @param {Object} body - Export options and filters.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to export job or file link.
     */
    exportCSVCustomers: (body, token = "") => {
        return axiosInstance(token).post(`/export/customers`, body);
    },

    /**
     * Export services to CSV via POST payload.
     * @param {Object} body - Export options and filters.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to export job or file link.
     */
    exportCSVServices: (body, token = "") => {
        return axiosInstance(token).post(`/export/services`, body);
    },

    /**
     * Export companies to CSV via POST payload.
     * @param {Object} body - Export options and filters.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to export job or file link.
     */
    exportCSVCompanies: (body, token = "") => {
        return axiosInstance(token).post(`/export/companies`, body);
    },

    /**
     * Export branches/destinations for a supplier.
     * @param {string} supplierId - Supplier identifier.
     * @param {Object} payload - Export options and filters.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to export job or file link.
     */
    exportCSVBranches: (supplierId, payload, token = "") => {
        return axiosInstance(token).post(`/export/suppliers/${supplierId}/destinations`, payload);
    },

    /**
     * Export operators to CSV.
     * @param {Object} body - Export options and filters.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to export job or file link.
     */
    exportCSVOperators: (body, token) => {
        return axiosInstance(token).post(`/export/operators`, body);
    },

    /**
     * Export reservations with query and body filters.
     * @param {string} query - Optional query string to append.
     * @param {Object} body - Export options and filters.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to export job or file link.
     */
    exportCSVReservations: (query, body, token = "") => {
        return axiosInstance(token).post(`/export/reservations${query ? `?${query}` : ""}`, body);
    },

    /**
     * Export all reservations (no filters).
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to export job or file link.
     */
    exportCSVReservationsAll: (token = "") => {
        return axiosInstance(token).get(`/export/reservations/all`);
    },

    /**
     * Export all reservations using advanced search query.
     * @param {string} query - Query string for advanced search.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to export job or file link.
     */
    exportCSVReservationsAllSearch: (query, token = "") => {
        return axiosInstance(token).get(`/export/reservations/all/search/advanced?${query}`);
    },

    /**
     * Export all reservations using simple search query.
     * @param {string} query - Search query string.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to export job or file link.
     */
    exportCSVReservationsAllQuery: (query, token = "") => {
        return axiosInstance(token).get(`/export/reservations/all/search?search=${query}`);
    },

    /**
     * Export areas to CSV.
     * @param {Object} body - Export options and filters.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to export job or file link.
     */
    exportCSVAreas: (body, token = "") => {
        return axiosInstance(token).post(`/export/areas`, body);
    },

    /**
     * Export users to CSV.
     * @param {Object} body - Export options and filters.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to export job or file link.
     */
    exportCSVUsers: (body, token = "") => {
        return axiosInstance(token).post(`/export/users`, body);
    },

    /**
     * Request the format template to update rates for a branch.
     * @param {string} branchId - Branch identifier.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to the format/template.
     */
    requestTheFormatToUpdateRates: (branchId, token = "") => {
        return axiosInstance(token).get(`/rates/branches/${branchId}/getformatToUpdateRates`);
    },

    /**
     * Upload and update rates using a file payload.
     * @param {FormData|Object} file - File payload (FormData) containing the rates file.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to the upload result.
     */
    updateRates: (file, token = "") => {
        return axiosInstance(token).post(`/rate/uploadedByBranch`, file);
    },

    /**
     * Export agents for a customer with optional query and payload filters.
     * @param {string} customerId - Customer identifier.
     * @param {string} query - Optional query string.
     * @param {Object} payload - Export options and filters.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to export job or file link.
     */
    exportCSVAgents: (customerId, query, payload, token = "") => {
        return axiosInstance(token).post(`/export/customers/${customerId}/agents${query ? `?${query}` : ""}`, payload);
    },
    /**
     * Export transfer reservations template for given reservation IDs.
     * @param {Array<string>} ids - Array of reservation IDs to include in the export.
     * @param {string} [token]
     * @returns {Promise} Axios promise resolving to the export file or job.
     */
    exportEventsTemplate: (ids = [], token = "") => {
        return axiosInstance(token).post(`/export-transfer-reservations`, { ids });
    }
}
